/**
 * Component for handling file inputs with dynamic previews,
 * drag & drop, and file management via DataTransfer API.
 */
export default class FileUploader {
    /**
     * @param {HTMLElement} element - The .form-field-file-upload container.
     */
    constructor(element) {
        if (!element) return;

        this.container = element;
        this.input = this.container.querySelector(".js-file-input");
        this.previewContainer = this.container.querySelector(".js-file-preview");
        this.dropZone = this.container.querySelector(".js-file-upload-zone");

        if (!this.input) return;

        // Configuration from data attributes
        this.isMultiple = this.input.hasAttribute("multiple");
        this.maxSizeMB = parseFloat(this.input.dataset.maxSize) || 10;
        this.maxFiles = parseInt(this.input.dataset.maxFiles, 10) || (this.isMultiple ? 5 : 1);
        this.acceptTypes = this.input.getAttribute("accept");

        // Flag to prevent infinite loops on programmatic dispatchEvent
        this.isSyncing = false;

        // Native DataTransfer object to manipulate file list dynamically
        this.dataTransfer = this.createDataTransfer();

        this.previewTemplate = document.querySelector("#file-preview-template");
        this.previewObjectUrls = new Set();

        this.init();
    }

    /**
     * Initializes component listeners.
     */
    init() {
        this.bindEvents();
    }

    /**
     * Binds event listeners for input change and Drag & Drop.
     */
    bindEvents() {
        // File selection handler
        this.input.addEventListener("change", (e) => {
            if (this.isSyncing) return;
            this.handleFiles(Array.from(e.target.files));
        });

        // Drag & Drop events
        if (this.dropZone) {
            ["dragenter", "dragover"].forEach((eventName) => {
                this.dropZone.addEventListener(eventName, (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.dropZone.classList.add("is-dragover");
                });
            });

            ["dragleave", "drop"].forEach((eventName) => {
                this.dropZone.addEventListener(eventName, (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.dropZone.classList.remove("is-dragover");
                });
            });

            this.dropZone.addEventListener("drop", (e) => {
                const droppedFiles = Array.from(e.dataTransfer.files);
                this.handleFiles(droppedFiles);
            });
        }

        // Remove file item click delegation
        if (this.previewContainer) {
            this.previewContainer.addEventListener("click", (e) => {
                const removeBtn = e.target.closest(".js-file-remove");
                if (removeBtn) {
                    const index = parseInt(removeBtn.dataset.index, 10);
                    this.removeFile(index);
                }
            });
        }

        this.input.form?.addEventListener("reset", () => {
            window.setTimeout(() => {
                this.dataTransfer = this.createDataTransfer();
                this.input.value = "";
                this.renderPreview();
            }, 0);
        });
    }

    /**
     * Creates a DataTransfer instance when the browser supports file-list editing.
     *
     * @returns {DataTransfer|null}
     */
    createDataTransfer() {
        try {
            return new DataTransfer();
        } catch {
            return null;
        }
    }

    /**
     * Process incoming files, validates limits, and appends them to DataTransfer.
     *
     * @param {File[]} incomingFiles
     */
    handleFiles(incomingFiles) {
        if (!incomingFiles.length) return;

        if (!this.dataTransfer) {
            const invalidFile = incomingFiles.find(
                (file) => !this.isValidFileType(file) || file.size > this.maxSizeMB * 1024 * 1024,
            );

            if (invalidFile) {
                this.input.value = "";
                this.renderPreview();
                alert(
                    !this.isValidFileType(invalidFile)
                        ? window.agds.i18n.invalidFileFormat.replace("%1$s", invalidFile.name)
                        : window.agds.i18n.maxFileSize
                              .replace("%1$s", invalidFile.name)
                              .replace("%2$s", this.maxSizeMB),
                );
                return;
            }

            this.renderPreview();
            return;
        }

        // If single mode, reset previous files
        if (!this.isMultiple) {
            this.dataTransfer = this.createDataTransfer();
            if (!this.dataTransfer) {
                this.handleFiles(incomingFiles);
                return;
            }
        }

        const currentCount = this.dataTransfer.files.length;
        let filesToAdd = incomingFiles;

        // Limit total count
        if (currentCount + filesToAdd.length > this.maxFiles) {
            const availableSlots = this.maxFiles - currentCount;
            alert(
                window.agds.i18n.maxFiles.replace("%d", this.maxFiles),
            );

            if (availableSlots <= 0) return;
            filesToAdd = filesToAdd.slice(0, availableSlots);
        }

        filesToAdd.forEach((file) => {
            if (!this.isValidFileType(file)) {
                alert(window.agds.i18n.invalidFileFormat.replace("%1$s", file.name));
                return;
            }

            // Validate file size
            if (file.size > this.maxSizeMB * 1024 * 1024) {
                alert(
                    window.agds.i18n.maxFileSize
                        .replace("%1$s", file.name)
                        .replace("%2$s", this.maxSizeMB),
                );
                return;
            }

            // Append to DataTransfer
            this.dataTransfer.items.add(file);
        });

        // Sync DataTransfer back to native input element
        this.syncInputFiles();

        // Render preview list
        this.renderPreview();
    }

    /**
     * Removes file at specific index from DataTransfer.
     *
     * @param {number} indexToTarget
     */
    removeFile(indexToTarget) {
        if (!this.dataTransfer) {
            this.input.value = "";
            this.renderPreview();
            return;
        }

        const newDt = new DataTransfer();
        const files = Array.from(this.dataTransfer.files);

        files.forEach((file, index) => {
            if (index !== indexToTarget) {
                newDt.items.add(file);
            }
        });

        this.dataTransfer = newDt;
        this.syncInputFiles();
        this.renderPreview();
    }

    /**
     * Syncs internal DataTransfer files to HTMLInputElement.files property.
     */
    syncInputFiles() {
        // Увімкнути захисний прапорець перед dispatchEvent
        this.isSyncing = true;

        try {
            this.input.files = this.dataTransfer.files;

            // Trigger input event for FormValidator if connected
            this.input.dispatchEvent(new Event("change", { bubbles: true }));
        } catch {
            this.dataTransfer = null;
        } finally {
            this.isSyncing = false;
        }
    }

    /**
     * Renders preview list in the preview container.
     */
    renderPreview() {
        if (!this.previewContainer || !this.previewTemplate) return;

        const files = Array.from(this.dataTransfer?.files ?? this.input.files ?? []);

        this.revokePreviewUrls();

        if (!files.length) {
            this.previewContainer.replaceChildren();
            this.container.classList.remove("has-files");

            return;
        }

        this.container.classList.add("has-files");

        const list = document.createElement("ul");
        list.className = "file-preview-list";

        const fragment = document.createDocumentFragment();

        files.forEach((file, index) => {
            const item = this.previewTemplate.content.cloneNode(true);
            const element = item.querySelector(".file-preview-item");
            const thumb = item.querySelector(".js-file-preview-thumb");
            const image = item.querySelector(".file-preview-thumb img");

            element.dataset.index = index;

            item.querySelector(".file-preview-name").textContent = file.name;
            item.querySelector(".file-preview-size").textContent = this.formatBytes(file.size);

            item.querySelector(".js-file-remove").dataset.index = index;

            if (file.type.startsWith("image/")) {
                const objectUrl = URL.createObjectURL(file);

                this.previewObjectUrls.add(objectUrl);

                image.src = objectUrl;
                image.alt = file.name;
            } else {
                thumb.remove();
            }

            fragment.appendChild(item);
        });

        list.appendChild(fragment);

        this.previewContainer.replaceChildren(list);
    }

    /**
     * Revokes object URLs for preview images.
     *
     * @returns {void}
     *
     */
    revokePreviewUrls() {
        this.previewObjectUrls.forEach((url) => {
            URL.revokeObjectURL(url);
        });

        this.previewObjectUrls.clear();
    }

    /**
     * Helper to format bytes to human readable form.
     */
    formatBytes(bytes, decimals = 1) {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    /**
     * Helper to escape HTML characters in file names.
     */
    escapeHtml(str) {
        return str.replace(
            /[&<>"']/g,
            (m) =>
                ({
                    "&": "&amp;",
                    "<": "&lt;",
                    ">": "&gt;",
                    '"': "&quot;",
                    "'": "&#039;",
                })[m],
        );
    }

    /**
     * Checks if a file matches the accept attribute.
     *
     * @param {File} file
     * @returns {boolean}
     */
    isValidFileType(file) {
        if (!this.acceptTypes) return true; // Якщо accept не задано, дозволяємо все

        const types = this.acceptTypes.split(",").map((type) => type.trim().toLowerCase());
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();

        return types.some((type) => {
            // Перевірка по розширенню (наприклад, .pdf або .jpg)
            if (type.startsWith(".")) {
                return fileName.endsWith(type);
            }
            // Перевірка по масці MIME (наприклад, image/*)
            if (type.endsWith("/*")) {
                const baseType = type.split("/")[0];
                return fileType.startsWith(baseType + "/");
            }
            // Точний збіг MIME (наприклад, application/pdf)
            return fileType === type;
        });
    }

    destroy() {
        this.container.remove();
        this.revokePreviewUrls();
    }
}
